"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 67: Sunsoft-3
 * ------------------------------------------------------------------ *
 * Fantasy Zone II (J), Mito Koumon II and Vs. Platoon are the games on this
 * board. What the chip actually does:
 *
 *   - one 16 KB PRG window at $8000, the last 16 KB bank fixed at $C000;
 *   - four 2 KB CHR windows at $0000, $0800, $1000 and $1800;
 *   - nametable mirroring selected by a register (V, H, 1-screen A/B);
 *   - a 16-bit down counter clocked by M2, i.e. once per CPU cycle, that
 *     wraps from $0000 to $FFFF, raises an IRQ and stops itself.
 *
 * Register map (https://www.nesdev.org/wiki/INES_Mapper_067):
 *
 *   $8000        acknowledge the pending IRQ (and its mirrors: the gaps
 *                between the register blocks below)
 *   $8800-$8FFF  CHR bank 0 (2 KB at $0000)
 *   $9800-$9FFF  CHR bank 1 (2 KB at $0800)
 *   $A800-$AFFF  CHR bank 2 (2 KB at $1000)
 *   $B800-$BFFF  CHR bank 3 (2 KB at $1800)
 *   $C800-$CFFF  IRQ counter, write twice: high byte then low byte
 *   $D800-$DFFF  IRQ enable (bit 4); the write resets the write-twice latch
 *   $E800-$EFFF  mirroring (0 vertical, 1 horizontal, 2 1-screen A, 3 1-screen B)
 *   $F800-$FFFF  PRG bank (bits 0-3)
 *
 * Every register is decoded with address & $F800, so the whole 2 KB block it
 * names answers, not just the first byte.
 *
 * The reset vector lives in the fixed last bank, so the game starts before it
 * has written a single register. Power-on values are bank 0 at $8000 and CHR
 * bank 0 in all four windows.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  Mappers[67] = function (nes) {
    this.nes = nes;
  };

  Mappers[67].prototype = new Mappers[0]();

  // The counter is clocked by M2, not by the PPU. Driven from the scanline hook
  // it would run about 113 times too slowly and every split would be late.
  Mappers[67].prototype.watchesCpuCycles = true;

  Mappers[67].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    this.prgBank = 0;
    this.chrBanks = [0, 0, 0, 0];
    this.mirroringReg = 0;

    this.irqEnabled = false;
    this.irqCounter = 0;
    this.irqLatchHigh = false; // false: the next $C800 write is the high byte
    this.irqPending = false;
  };

  Mappers[67].prototype.write = function (address, value) {
    if (address < 0x8000) {
      // Work RAM, PPU/APU registers and cartridge WRAM are NROM's business.
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    switch (address & 0xf800) {
      case 0x8800:
        this.chrBanks[0] = value & 0x3f; // the chip has six CHR pins
        this.load2kVromBank(this.chrBanks[0], 0x0000);
        return;

      case 0x9800:
        this.chrBanks[1] = value & 0x3f;
        this.load2kVromBank(this.chrBanks[1], 0x0800);
        return;

      case 0xa800:
        this.chrBanks[2] = value & 0x3f;
        this.load2kVromBank(this.chrBanks[2], 0x1000);
        return;

      case 0xb800:
        this.chrBanks[3] = value & 0x3f;
        this.load2kVromBank(this.chrBanks[3], 0x1800);
        return;

      case 0xc800:
        // Write-twice register: the first write lands in the high byte, the
        // second in the low byte. It changes the live counter directly; it is
        // not a reload latch.
        if (this.irqLatchHigh) {
          this.irqCounter = (this.irqCounter & 0xff00) | (value & 0xff);
        } else {
          this.irqCounter = (this.irqCounter & 0x00ff) | ((value & 0xff) << 8);
        }
        this.irqLatchHigh = !this.irqLatchHigh;
        return;

      case 0xd800:
        // Bit 4 starts and stops the counter. The write also puts the
        // write-twice latch back on the high byte.
        this.irqEnabled = (value & 0x10) !== 0;
        this.irqLatchHigh = false;
        this.acknowledgeIrq();
        return;

      case 0xe800:
        this.mirroringReg = value & 0x03;
        this.updateMirroring();
        return;

      case 0xf800:
        // Bits 0-3 select a 16 KB bank at $8000. Bit 4 is a latch the board
        // does not use.
        this.prgBank = value & 0x0f;
        this.loadRomBank(this.prgBank, 0x8000);
        return;

      default:
        // $8000-$87FF and the unused gaps between the register blocks are the
        // interrupt acknowledge. $C000-$C7FF, $D000-$D7FF, ... land here too,
        // exactly as the wiki's $8800 mask describes.
        this.acknowledgeIrq();
        return;
    }
  };

  Mappers[67].prototype.updateMirroring = function () {
    switch (this.mirroringReg & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN);
        break;
      default:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN_ALT);
        break;
    }
  };

  Mappers[67].prototype.updateBanks = function () {
    var rom = this.nes.rom;
    if (!rom || rom.romCount === 0) {
      return;
    }

    this.loadRomBank(this.prgBank, 0x8000);
    // $C000 is the last 16 KB bank on the board, not a switchable window.
    this.loadRomBank(rom.romCount - 1, 0xc000);

    for (var i = 0; i < 4; i++) {
      this.load2kVromBank(this.chrBanks[i], i * 0x0800);
    }
  };

  // One burst of CPU cycles. The counter counts down while enabled and the
  // wrap from $0000 to $FFFF is what fires the IRQ -- that is why a game writes
  // "cycles wanted minus one" into $C800.
  Mappers[67].prototype.clockCpuCycles = function (cycles) {
    if (!this.irqEnabled || cycles <= 0) {
      return;
    }

    if (this.irqCounter === 0) {
      // The very next cycle wraps.
      this.irqCounter = 0xffff;
      this.irqEnabled = false;
      this.raiseIrq();
      return;
    }

    if (cycles > this.irqCounter) {
      // The counter reaches zero, then one more cycle wraps it to $FFFF.
      this.irqCounter = 0xffff;
      this.irqEnabled = false;
      this.raiseIrq();
    } else {
      this.irqCounter -= cycles;
    }
  };

  Mappers[67].prototype.raiseIrq = function () {
    if (this.irqPending) {
      return;
    }
    this.irqPending = true;
    this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
  };

  Mappers[67].prototype.acknowledgeIrq = function () {
    this.irqPending = false;
  };

  Mappers[67].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Mapper 67: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on layout: PRG bank 0 at $8000, last bank at $C000, CHR bank 0 in
    // every window, vertical mirroring (the register's reset value).
    this.updateBanks();
    this.updateMirroring();

    this.resetIrqHandler();
  };

  Mappers[67].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank = this.prgBank;
    s.chrBanks = this.chrBanks.slice();
    s.mirroringReg = this.mirroringReg;
    s.irqEnabled = this.irqEnabled;
    s.irqCounter = this.irqCounter;
    s.irqLatchHigh = this.irqLatchHigh;
    s.irqPending = this.irqPending;
    return s;
  };

  Mappers[67].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank = s.prgBank;
    this.chrBanks = s.chrBanks.slice();
    this.mirroringReg = s.mirroringReg;
    this.irqEnabled = s.irqEnabled;
    this.irqCounter = s.irqCounter;
    this.irqLatchHigh = s.irqLatchHigh;
    this.irqPending = s.irqPending;

    // The CPU and PPU keep their own copies of the windows.
    this.updateBanks();
    this.updateMirroring();
  };
}

module.exports = { register: register };
